import { type SieveFilterModel } from '~/util/sieve/filter';
import { type SearchOptionType, type SearchTypeToModel } from '@generated';
import { DataTable, type DataTableProps } from '../DataTable';
import { ExportButton } from './export/ExportButton';
import { useServerDataTable, type QueryDataTableColumn, type QueryDataTableType } from './useServerDataTable';

export type QueryDataTableRow<T extends SearchOptionType> = SearchTypeToModel[T];

export type QueryDataTableGrid<T extends SearchOptionType = SearchOptionType> = { searchType: T };

export type RowOfQueryDataTable<G extends QueryDataTableGrid> = QueryDataTableRow<G['searchType']>;

export type PropsOfQueryDataTable<G extends QueryDataTableGrid> = Omit<QueryDataTableProps<G['searchType']>, 'columns'>;

export interface QueryDataTableProps<T extends SearchOptionType>
	extends Omit<DataTableProps<SearchTypeToModel[T]>, 'columns' | 'rows' | 'isLoading' | 'pager' | 'sortDescriptor' | 'onSortChange'> {
	columns: QueryDataTableColumn<T>[];
	type?: QueryDataTableType;
	staticFilters?: SieveFilterModel[];
	enabled?: boolean;
	pageSize?: number;
	refetchInterval?: number | false;
	exportable?: boolean;
	exportFileName?: string;
	hideColumns?: string[];
}

export function QueryDataTable<T extends SearchOptionType>({
	searchType,
	columns,
	type,
	staticFilters,
	enabled,
	pageSize,
	refetchInterval,
	exportable,
	exportFileName,
	...props
}: QueryDataTableProps<T> & { searchType: T }) {
	const table = useServerDataTable(searchType, { columns, type, staticFilters, enabled, pageSize, refetchInterval });

	return (
		<div className="flex flex-col gap-2">
			{exportable && (
				<div className="mb-2 flex justify-end">
					<ExportButton searchType={searchType} columns={columns} fileName={exportFileName} {...table.exportState} />
				</div>
			)}

			<DataTable
				key={searchType}
				columns={columns}
				rows={table.rows}
				isLoading={table.isLoading}
				pager={table.pager}
				sortDescriptor={table.sortDescriptor}
				onSortChange={table.onSortChange}
				{...props}
			/>
		</div>
	);
}

export function createQueryDataTable<T extends SearchOptionType>(searchType: T) {
	return {
		searchType,
		columns: (cols: QueryDataTableColumn<T>[]) => cols,
		Table: (props: QueryDataTableProps<T>) => <QueryDataTable<T> {...props} searchType={searchType} />
	};
}
